/**
 * Android: a video call answered on the locked keyguard connects audio only.
 * Camera send and remote video start once the device unlocks — the app is
 * behind the keyguard until then, and nothing may run the camera in a pocket.
 *
 * Tracks the one call a locked answer belongs to. The unlock can land before
 * the audio-only join finishes, so video starts from whichever comes last.
 */
export class LockedAnswerVideoGate {
  private callId: string | null = null
  private unlocked = false
  private deferred = false
  private joined = false
  private started = false

  /**
   * A native answer was made on the locked keyguard. Idempotent per call: the
   * same answer also arrives again from the persisted pending-action drain.
   */
  noteLockedAnswer(callId: string): void {
    if (this.callId === callId) return
    this.callId = callId
    this.unlocked = false
    this.deferred = false
    this.joined = false
    this.started = false
  }

  /** True while `callId`'s video waits for the unlock (skip the camera pre-flight). */
  isWaiting(callId: string): boolean {
    return this.callId === callId && !this.unlocked
  }

  /** Join start: true when `callId` must join audio-only. */
  deferAtJoin(callId: string): boolean {
    if (!this.isWaiting(callId)) return false
    this.deferred = true
    return true
  }

  /** The audio-only join finished: true when the device already unlocked (start video now). */
  noteJoined(callId: string): boolean {
    if (this.callId !== callId || !this.deferred) return false
    this.joined = true
    return this.unlocked && this.takeStart()
  }

  /** The device unlocked: true when the audio-only join is done (start video now). */
  noteUnlocked(callId: string): boolean {
    if (this.callId !== callId) return false
    this.unlocked = true
    return this.joined && this.takeStart()
  }

  private takeStart(): boolean {
    if (this.started) return false
    this.started = true
    return true
  }
}
